import { XIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { ErrorBoundary } from '@sentry/react';
import classNames from 'classnames';
import React, { FunctionComponent, useContext } from 'react';

import { CompactIconButton } from '@/core/buttons/IconButton';
import { DirtyFormContext } from '@/core/DirtyFormContext';
import { ErrorMessage } from '@/ErrorMessage';
import { translate } from '@/i18n';

import { DrawerContext } from './DrawerContext';

export const DrawerRoot: FunctionComponent = () => {
  const context = useContext(DrawerContext);

  if (!context) {
    return null;
  }

  const { isOpen, drawerComponent, drawerProps, closeDrawer } = context;
  const [isDirtyContext, setIsDirtyContext] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const insidePointerDownRef = React.useRef<Event | null>(null);

  // Radix decides whether a pointerdown belongs to the layer from the React
  // tree, not the DOM: it flags the pointer as inside from an
  // onPointerDownCapture on the content. Anything portalled in from a tree
  // outside the dialog therefore reads as outside however deeply the DOM nests
  // it — which is how the docked Matrix call, owned by MatrixCallHost so it
  // survives moving between destinations, dismissed the whole drawer on the
  // first click on its own mic/camera/screen-share controls.
  //
  // So record DOM containment ourselves, from a native capture listener that
  // runs while the event is still travelling down. Testing the target later,
  // inside onPointerDownOutside, is too late: React has flushed the re-render
  // the same pointerdown triggered, and the node the pointer hit — the icon
  // inside the control — is already detached, so contains() says false.
  React.useEffect(() => {
    const remember = (event: Event) => {
      const target = event.target as Node | null;
      if (target && contentRef.current?.contains(target)) {
        insidePointerDownRef.current = event;
      }
    };
    document.addEventListener('pointerdown', remember, true);
    return () => document.removeEventListener('pointerdown', remember, true);
  }, []);
  const isDirtyForm = isDirtyContext;
  const onHide = () => {
    if (
      isDirtyForm &&
      !confirm(
        translate(
          'You have entered data in form. When drawer is closed form data would be lost.',
        ),
      )
    ) {
      return;
    }
    closeDrawer();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
    >
      {isOpen && <Dialog.Overlay className="drawer-overlay" />}
      {/*
        No forceMount: Dialog.Content's own Presence (gated on the Root's
        `open`) keeps #kt_drawer mounted through the close animation on its
        own, as long as an actual @keyframes animation-name changes between
        the .drawer-on and non-.drawer-on states (see the #kt_drawer rules in
        _shell.scss) — Presence detects animations, not CSS transitions.
        forceMount was tried first and reverted: it keeps the Radix
        DismissableLayer's global pointerdown/focus listeners permanently
        registered even while closed, which broke real (trusted) clicks on
        the header's Support/Pending-tasks toggle buttons elsewhere on the
        page — untrusted/synthetic clicks and every other manual DOM check
        looked fine, which is why it wasn't caught until real click testing.
      */}
      <Dialog.Content
        ref={contentRef}
        id="kt_drawer"
        className={classNames(
          'bg-body drawer drawer-end',
          drawerProps.shellClass,
          { 'drawer-on': isOpen },
        )}
        style={{ '--drawer-width': drawerProps.width } as React.CSSProperties}
        aria-describedby={undefined}
        onPointerDownOutside={(event) => {
          if (event.detail.originalEvent === insidePointerDownRef.current) {
            event.preventDefault();
          }
          // A floating drawer leaves the header's drawer toggles live (see
          // _shell.scss). Their click closes or switches the drawer; dismissing
          // it here first would have that same click open it again.
          const target = event.detail.originalEvent.target as Element | null;
          if (
            drawerProps.shellClass &&
            target?.closest('[data-drawer-toggle]')
          ) {
            event.preventDefault();
          }
        }}
      >
        <div className="card shadow-none rounded-0 w-100">
          <div className="card-header" id="kt_drawer_header">
            <div>
              <Dialog.Title asChild>
                <h3 className="card-title fw-bolder text-dark fs-3">
                  {drawerProps.title}
                </h3>
              </Dialog.Title>
              {drawerProps.subtitle && (
                <h6 className="text-muted card-subtitle fw-bold">
                  {drawerProps.subtitle}
                </h6>
              )}
            </div>

            <div className="card-toolbar gap-2">
              {drawerProps.toolbar ? (
                React.createElement(drawerProps.toolbar, { close: onHide })
              ) : (
                <CompactIconButton
                  iconNode={<XIcon weight="bold" />}
                  tooltip={translate('Close')}
                  onClick={onHide}
                  tooltipPlacement="bottom"
                />
              )}
            </div>
          </div>
          <div className="card-body scroll-y p-0" id="kt_drawer_body">
            <DirtyFormContext.Provider
              value={{ setIsDirty: setIsDirtyContext }}
            >
              <div className={drawerProps.bodyClassName ?? 'p-8'}>
                <ErrorBoundary fallback={ErrorMessage}>
                  {drawerComponent
                    ? React.createElement(drawerComponent, {
                        ...drawerProps,
                        close: onHide,
                      })
                    : null}
                </ErrorBoundary>
              </div>
            </DirtyFormContext.Provider>
          </div>
          {drawerProps.footer && (
            <div className="card-footer py-5 text-center" id="kt_drawer_footer">
              <drawerProps.footer {...drawerProps} close={onHide} />
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};
